<template>
  <div class="row mt-1 mx-1">
    <div ref="tableScrollEl" class="table-responsive mx-0 px-0 timetable-scroll">
      <table class="table table-sm table-bordered align-middle text-center timetable-table">
        <thead>
          <tr>
            <th class="timetable-time-col">開始</th>
            <th v-for="court in courts" :key="court" class="timetable-court-col">
              コート<br />{{ court }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="start in startTimes" :key="start">
            <th class="timetable-time-col">{{ start }}</th>
            <td v-for="court in courts" :key="`${start}-${court}`" class="timetable-cell" :class="[getCellClass(getMatch(start, court)), { 'cell-selected': isSelected(start, court) }]" @click="onCellClick(start, court)" @dblclick="onCellDblClick(start, court)">
              <template v-if="getMatch(start, court)">
                <div class="timetable-match-id">
                  {{ getMatch(start, court).event_id }}-{{ getMatch(start, court).match_no }}
                  <font-awesome-icon v-if="hasMatchNote(getMatch(start, court))" :icon="['fas', 'circle-info']" class="info-icon" />
                </div>
                <div class="actual-court-badge" v-if="getMatch(start, court).actual_court">{{ getMatch(start, court).actual_court }}</div>
                <div class="timetable-cell-content">
                  <div class="timetable-side">
                    <div class="timetable-team">
                      <TimeTableTeamLabelComponent :teamId="getMatch(start, court).left_team_id_1" :alterText="getMatch(start, court).left_entry_id" />
                      <span v-if="getMatch(start, court).left_team_id_2 != '' && getMatch(start, court).left_team_id_1 != getMatch(start, court).left_team_id_2">
                        <br /><TimeTableTeamLabelComponent :teamId="getMatch(start, court).left_team_id_2" />
                      </span>
                    </div>
                    <div class="timetable-players">
                      <div class="timetable-player">
                        {{ getMatch(start, court).left_player_name_1 }}
                        <span v-if="isWinner(getMatch(start, court), 'left')" class="winner-icon">
                          <font-awesome-icon :icon="['fas', 'circle-check']" style="color:lightgreen"/>
                        </span>
                      </div>
                      <div class="timetable-player">{{ getMatch(start, court).left_player_name_2 }}</div>
                    </div>
                  </div>

                  <div class="timetable-vs">vs</div>

                  <div class="timetable-side">
                    <div class="timetable-team">
                      <TimeTableTeamLabelComponent :teamId="getMatch(start, court).right_team_id_1" :alterText="getMatch(start, court).right_entry_id" />
                      <span v-if="getMatch(start, court).right_team_id_2 != '' && getMatch(start, court).right_team_id_1 != getMatch(start, court).right_team_id_2">
                        <br /><TimeTableTeamLabelComponent :teamId="getMatch(start, court).right_team_id_2" />
                      </span>
                    </div>
                    <div class="timetable-players">
                      <div class="timetable-player">
                        {{ getMatch(start, court).right_player_name_1 }}
                        <span v-if="isWinner(getMatch(start, court), 'right')" class="winner-icon">
                          <font-awesome-icon :icon="['fas', 'circle-check']" style="color:lightgreen"/>
                        </span>
                      </div>
                      <div class="timetable-player">{{ getMatch(start, court).right_player_name_2 }}</div>
                    </div>
                  </div>
                </div>
              </template>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th class="timetable-time-col">開始</th>
            <th v-for="court in courts" :key="court" class="timetable-court-col">
              コート<br />{{ court }}
            </th>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>

  <div ref="bottomScrollEl" class="timetable-bottom-scroll" aria-hidden="true">
    <div class="timetable-bottom-scroll-spacer" :style="{ width: `${bottomScrollWidth}px` }"></div>
  </div>

  <div v-if="selectedCount > 0" class="selection-fab-area">
    <div v-if="!showActions" class="selection-fab" @click="showActions = true">
      <font-awesome-icon :icon="['fas', 'check']" />
      <span class="selection-fab-count">{{ selectedCount }}</span>
    </div>
    <div v-else class="action-buttons">
      <button class="action-btn action-btn-waiting" @click="updateSelectedStatus('waiting')">待機</button>
      <button class="action-btn action-btn-called" @click="updateSelectedStatus('called')">招集中</button>
      <button class="action-btn action-btn-called-finished" @click="updateSelectedStatus('called_finished')">招集済み</button>
      <button class="action-btn action-btn-playing" @click="updateSelectedStatus('playing')">試合中</button>
      <button class="action-btn action-btn-cancel" @click="showActions = false">キャンセル</button>
    </div>
  </div>

  <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
    <div class="modal-card-wrapper">
      <button class="modal-close-btn" @click="closeModal">
        <font-awesome-icon :icon="['fas', 'xmark']" />
      </button>
      <MatchCardComponent :match="modalMatch" :showKana="true" :entries="entries" />
    </div>
  </div>

  <FooterComponent />
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import TimeTableTeamLabelComponent from '@/components/TimeTableTeamLabelComponent.vue'
import MatchCardComponent from '@/components/MatchCardComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import { ref as fbRef, getDatabase, onValue, query, orderByChild, startAt, endAt, update } from 'firebase/database'
import { getAuth, onAuthStateChanged } from 'firebase/auth'

const db = getDatabase()

const isLoggedIn = ref(false)

const realtimeMatches = ref({})
const realtime1 = ref({})
const realtime2 = ref({})
const realtime3 = ref({})
const realtime4 = ref({})

const entries = ref({})

const tableScrollEl = ref(null)
const bottomScrollEl = ref(null)
const bottomScrollWidth = ref(0)
let resizeObserver = null
let isSyncingScroll = false

const establishConnection = () => {
  const matchesRef = fbRef(db, '/2026-summer/matches/')
  const entriesRef = fbRef(db, '/2026-summer/entries')

  // entriesデータを取得
  onValue(entriesRef, (snapshot) => {
    entries.value = snapshot.val() || {}
  })

  onValue(query(matchesRef, orderByChild('timetable_index'), startAt(900000), endAt(1145999)), (snapshot) => {
    realtime1.value = snapshot.val() || {}
    updateMatches()
  })
  onValue(query(matchesRef, orderByChild('timetable_index'), startAt(1146000), endAt(1245999)), (snapshot) => {
    realtime2.value = snapshot.val() || {}
    updateMatches()
  })
  onValue(query(matchesRef, orderByChild('timetable_index'), startAt(1246000), endAt(1445999)), (snapshot) => {
    realtime3.value = snapshot.val() || {}
    updateMatches()
  })
  onValue(query(matchesRef, orderByChild('timetable_index'), startAt(1446000)), (snapshot) => {
    realtime4.value = snapshot.val() || {}
    updateMatches()
  })
}

const updateMatches = () => {
  realtimeMatches.value = { ...realtime1.value, ...realtime2.value, ...realtime3.value, ...realtime4.value }
}


const fetchTeams = () => {
  onValue(fbRef(db, '/2026-summer/teams'), (snapshot) => {
    if (snapshot.exists()) {
      localStorage.setItem('teams', JSON.stringify(snapshot.val()))
    }
  })
}

onMounted(() => {
  const auth = getAuth()
  isLoggedIn.value = !!auth.currentUser
  onAuthStateChanged(auth, (user) => {
    isLoggedIn.value = !!user
  })

  establishConnection()
  fetchTeams()

  nextTick(() => {
    const tableEl = tableScrollEl.value
    const bottomEl = bottomScrollEl.value
    if (!tableEl || !bottomEl) return

    const updateWidth = () => {
      bottomScrollWidth.value = tableEl.scrollWidth
    }

    updateWidth()
    resizeObserver = new ResizeObserver(() => updateWidth())
    resizeObserver.observe(tableEl)

    const onTableScroll = () => {
      if (isSyncingScroll) return
      isSyncingScroll = true
      bottomEl.scrollLeft = tableEl.scrollLeft
      isSyncingScroll = false
    }

    const onBottomScroll = () => {
      if (isSyncingScroll) return
      isSyncingScroll = true
      tableEl.scrollLeft = bottomEl.scrollLeft
      isSyncingScroll = false
    }

    tableEl.addEventListener('scroll', onTableScroll, { passive: true })
    bottomEl.addEventListener('scroll', onBottomScroll, { passive: true })

    onBeforeUnmount(() => {
      tableEl.removeEventListener('scroll', onTableScroll)
      bottomEl.removeEventListener('scroll', onBottomScroll)
    })
  })
})

onBeforeUnmount(() => {
  if (resizeObserver && tableScrollEl.value) {
    resizeObserver.unobserve(tableScrollEl.value)
    resizeObserver.disconnect()
  }
  resizeObserver = null
})

const matchList = computed(() => Object.values(realtimeMatches.value || {}))

const startTimes = computed(() => {
  const starts = new Set(
    matchList.value
      .map((m) => (m?.start ?? '').toString())
      .filter((s) => s !== '')
  )
  return Array.from(starts).sort((a, b) => {
    const na = parseInt(a.replace(/:/g, ''), 10)
    const nb = parseInt(b.replace(/:/g, ''), 10)
    if (na < nb) return -1
    if (na > nb) return 1
    return a.localeCompare(b)
  })
})

const courts = computed(() => {
  const cs = new Set(
    matchList.value
      .map((m) => (m?.court ?? '').toString())
      .filter((c) => c !== '')
  )
  return Array.from(cs).sort((a, b) => {
    const na = Number(a)
    const nb = Number(b)
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb
    return a.localeCompare(b)
  })
})

const matchMap = computed(() => {
  const map = new Map()
  for (const m of matchList.value) {
    const start = (m?.start ?? '').toString()
    const court = (m?.court ?? '').toString()
    if (!start || !court) continue
    const key = `${start}__${court}`
    if (!map.has(key)) map.set(key, m)
  }
  return map
})

const getMatch = (start, court) => {
  return matchMap.value.get(`${start}__${court}`) || null
}

const hasMatchNote = (match) => {
  if (!match) return false
  return !!match.note
}

const getCellClass = (match) => {
  if (!match) return ''
  switch (match.match_status) {
    case 'called':
      return 'cell-called'
    case 'called_finished':
      return 'cell-called-finished'
    case 'playing':
      return 'cell-playing'
    case 'finished':
      return 'cell-finished'
    case 'waiting':
      return 'cell-waiting'
    case '':
    case null:
    case undefined:
      return 'cell-waiting'
    default:
      return ''
  }
}

const isWinner = (match, side) => {
  if (!match) return false
  if (match.match_status !== 'finished') return false
  if (!match.winner_entry_id) return false
  if (side === 'left') return match.left_entry_id === match.winner_entry_id
  if (side === 'right') return match.right_entry_id === match.winner_entry_id
  return false
}

const showModal = ref(false)
const modalMatch = ref(null)
let clickTimer = null

const onCellClick = (start, court) => {
  if (clickTimer) clearTimeout(clickTimer)
  clickTimer = setTimeout(() => {
    const match = getMatch(start, court)
    if (!match) return
    modalMatch.value = match
    showModal.value = true
  }, 250)
}

const onCellDblClick = (start, court) => {
  if (clickTimer) {
    clearTimeout(clickTimer)
    clickTimer = null
  }
  if (!isLoggedIn.value) return
  toggleSelect(start, court)
}

const closeModal = () => {
  showModal.value = false
  modalMatch.value = null
}

const selectedCells = ref(new Set())

const toggleSelect = (start, court) => {
  const key = `${start}__${court}`
  const match = getMatch(start, court)
  if (!match) return
  if (match.match_status === 'finished') return
  const next = new Set(selectedCells.value)
  if (next.has(key)) {
    next.delete(key)
  } else {
    next.add(key)
  }
  selectedCells.value = next
  if (navigator.vibrate) navigator.vibrate(next.has(key) ? 100 : [100, 50, 100])
}

const isSelected = (start, court) => {
  return selectedCells.value.has(`${start}__${court}`)
}

const selectedCount = computed(() => selectedCells.value.size)

const showActions = ref(false)

const clearSelection = () => {
  selectedCells.value = new Set()
  showActions.value = false
}

const updateSelectedStatus = async (status) => {
  const updates = {}
  for (const key of selectedCells.value) {
    const [start, court] = key.split('__')
    const match = getMatch(start, court)
    if (!match) continue
    const matchKey = `${match.event_id}-${match.match_no}`
    updates[`/2026-summer/matches/${matchKey}/match_status`] = status
  }
  try {
    await update(fbRef(db), updates)
    if (navigator.vibrate) navigator.vibrate(300)
  } catch (e) {
    console.error('Failed to update match status:', e)
  }
  clearSelection()
}
</script>

<style scoped>
.timetable-scroll {
  height: calc(100vh - 18px);
  overflow-y: auto;
  overflow-x: auto;
  padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
}

.timetable-scroll::-webkit-scrollbar {
  height: 0;
}

.timetable-scroll {
  scrollbar-width: none;
}

.timetable-bottom-scroll {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 28px;
  overflow-x: auto;
  overflow-y: hidden;
  background-color: #ffffff;
  z-index: 2;
  -webkit-overflow-scrolling: touch;
}

.timetable-bottom-scroll-spacer {
  height: 1px;
}

.timetable-table {
  --bs-border-color: #b8b8b8;
}

.timetable-table thead th {
  position: sticky;
  top: 0;
  z-index: 3;
}

.timetable-table th.timetable-time-col {
  position: sticky;
  left: 0;
  z-index: 3;
}

.timetable-table thead th.timetable-time-col {
  z-index: 4;
}

.timetable-time-col {
  width: 64px;
  background-color: #e9ecef;
  white-space: nowrap;
}

.timetable-court-col {
  min-width: 160px;
  background-color: #e9ecef;
  white-space: nowrap;
}

.timetable-match-id {
  font-size: 0.9rem;
  color: #6c757d;
  text-align: left;
  margin-bottom: 0;
  line-height: 1.0;
  white-space: nowrap;
}

.timetable-team {
  line-height: 1.0;
}

.winner-icon {
  margin-left: 1px;
  color: #b8b8b8;
  font-size: 0.7rem;
}

.timetable-cell {
  padding: 3px;
  vertical-align: top;
}

.timetable-cell-content {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 6px;
  align-items: start;
}

.timetable-side {
  text-align: left;
}

.timetable-vs {
  font-size: 0.75rem;
  color: #6c757d;
  padding-top: 2px;
  white-space: nowrap;
}

.timetable-player {
  font-size: 0.85rem;
  line-height: 1.1;
  white-space: nowrap;
}

.cell-waiting {
  background-color: #ffffff;
}

.cell-called {
  background-color: #fff9db;
}

.cell-called-finished {
  background-color: peachpuff;
}

.cell-playing {
  background-color: #d1faff;
}

.cell-finished {
  background-color: #f0fff0;
}

.cell-selected {
  outline: 3px solid #0d6efd;
  outline-offset: -3px;
}

.timetable-cell {
  position: relative;
}

.selection-fab-area {
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 11;
  user-select: none;
}

.selection-fab {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #0d6efd;
  color: #fff;
  padding: 10px 20px;
  border-radius: 24px;
  font-size: 1rem;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  cursor: pointer;
}

.selection-fab-count {
  font-size: 1rem;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-btn {
  border: none;
  color: #fff;
  padding: 10px 16px;
  border-radius: 24px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  white-space: nowrap;
}

.action-btn-waiting {
  background: #6c757d;
}

.action-btn-called {
  background: #d4a017;
}

.action-btn-called-finished {
  background: #ffc89e;
  color: #000;
}

.action-btn-playing {
  background: #0dcaf0;
  color: #000;
}

.action-btn-cancel {
  background: #adb5bd;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
}

.modal-card-wrapper {
  position: relative;
  width: 90%;
  max-width: 420px;
}

.modal-close-btn {
  position: absolute;
  top: -12px;
  right: -12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #333;
  color: #fff;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 101;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.actual-court-badge {
  position: absolute;
  top: 0px;
  right: 0px;
  background: #d1e7dd;
  font-size: 0.9rem;
  font-weight: bold;
  border-radius: 1px;
  z-index: 2;
  width: 25px;
  height: 20px;
}

.info-icon {
  margin-left: 2px;
  color: #6c757d;
  font-size: 0.8rem;
}
</style>
